// src/layouts/commit/utils/productDataHelpers.js

const createCanonicalKey = (idFieldList) => {
  if (!idFieldList || typeof idFieldList !== 'object' || Object.keys(idFieldList).length === 0) {
    return null;
  }
  return Object.keys(idFieldList)
    .sort()
    .map((key) => `${key}=${idFieldList[key]}`)
    .join('&');
};

export const areProductsEqual = (productA, productB) => {
  if (!productA || !productB) {
    return false;
  }

  // Priority 1: Match using the canonical key from IDFieldList (most reliable)
  const keyA = createCanonicalKey(productA.IDFieldList);
  const keyB = createCanonicalKey(productB.IDFieldList);
  if (keyA && keyB && keyA === keyB) {
    return true;
  }

  // Priority 2: Match using Salesforce-provided IDs (very reliable)
  const idA = productA.id || productA.external_id || productA.sfId || productA.sf_id;
  const idB = productB.id || productB.external_id || productB.sfId || productB.sf_id;
  if (idA && idB && idA === idB) {
    return true;
  }

  // Priority 3: Fallback to matching by name (less reliable but good for older data)
  if (productA.name && productB.name && productA.name === productB.name) {
    return true;
  }

  return false;
};

/**
 * Checks if a product from one source (e.g., database) matches a product from another source (e.g., Salesforce)
 * by comparing IDs, handling property name variations (sf_id vs sfId, etc.) and ignoring version numbers.
 * This is specifically useful for intersecting products from different data sources where property names may differ.
 * 
 * @param {object} productA - First product to compare (e.g., from database with sf_id)
 * @param {object} productB - Second product to compare (e.g., from Salesforce with sfId)
 * @returns {boolean} True if products represent the same product (ignoring version)
 */
export const areProductsSameEntity = (productA, productB, enableLogging = false) => {
  if (!productA || !productB) {
    return false;
  }

  // Priority 1: Match using the canonical key from IDFieldList (most reliable)
  const keyA = createCanonicalKey(productA.IDFieldList);
  const keyB = createCanonicalKey(productB.IDFieldList);
  if (keyA && keyB && keyA === keyB) {
    if (enableLogging) console.log('[areProductsSameEntity] Matched via IDFieldList');
    return true;
  }

  // Priority 2: Match using Salesforce-provided IDs (handle property name variations)
  // Database products may use: id, external_id, sf_id
  // Salesforce products may use: id, external_id, sfId
  const dbIdA = productA.id || productA.external_id || productA.sf_id || productA.sfId;
  const dbIdB = productB.id || productB.external_id || productB.sf_id || productB.sfId;
  
  if (enableLogging) {
    console.log('[areProductsSameEntity] Comparing IDs:', {
      productA: { name: productA.name, id: productA.id, external_id: productA.external_id, sf_id: productA.sf_id, sfId: productA.sfId, computed: dbIdA },
      productB: { name: productB.name, id: productB.id, external_id: productB.external_id, sf_id: productB.sf_id, sfId: productB.sfId, computed: dbIdB }
    });
  }
  
  if (dbIdA && dbIdB && dbIdA === dbIdB) {
    if (enableLogging) console.log('[areProductsSameEntity] Matched via ID');
    return true;
  }

  // Priority 3: Fallback to matching by name (less reliable but good for older data)
  if (productA.name && productB.name && productA.name === productB.name) {
    if (enableLogging) console.log('[areProductsSameEntity] Matched via name');
    return true;
  }

  if (enableLogging) console.log('[areProductsSameEntity] NO MATCH');
  return false;
};

export const attachDataByCanonicalKey = (products, dataToAttach, attachmentProperty, dataExtractor) => {
  const defaultValue = Array.isArray(dataExtractor({})) ? [] : null;
  if (!dataToAttach || dataToAttach.length === 0) {
    return products.map((p) => ({
      ...p,
      related_data: { ...(p.related_data || {}), [attachmentProperty]: defaultValue },
    }));
  }
  const dataMap = new Map();
  dataToAttach.forEach((item) => {
    const key = createCanonicalKey(item.IDFieldList);
    if (key) dataMap.set(key, dataExtractor(item));
  });
  return products.map((p) => {
    const key = createCanonicalKey(p.IDFieldList);
    const matchingData = dataMap.get(key);
    return {
      ...p,
      related_data: { ...(p.related_data || {}), [attachmentProperty]: matchingData || defaultValue },
    };
  });
};

export const matchPricingModelsToProducts = (products, pricingModelData) => {
  const pricingModelMap = new Map();
  if (pricingModelData && pricingModelData.length > 0) {
    pricingModelData.forEach((model) => {
      const key = createCanonicalKey(model.IDFieldList);
      if (key) {
        pricingModelMap.set(key, {
          priceDimensions: model.priceDimensions || [],
          blockPrices: model.blockPrices || [],
          costs: model.costs || [],
        });
      }
    });
  }
  return products.map((product) => {
    const key = createCanonicalKey(product.IDFieldList);
    const matchingModel = pricingModelMap.get(key);
    return {
      ...product,
      related_data: {
        ...(product.related_data || {}),
        pricingModel: matchingModel || { priceDimensions: [], blockPrices: [], costs: [] },
      },
    };
  });
};

export const matchConsumptionToProducts = (products, consumptionData) => {
  if (!consumptionData) return products;
  console.log('matchConsumptionToProducts', consumptionData);
  const consumptionMap = new Map();
  if (consumptionData.consumptionSchedules && consumptionData.consumptionSchedules.length > 0) {
    consumptionData.consumptionSchedules.forEach((item) => {
      if (item.productName) consumptionMap.set(item.productName, item);
    });
  }
  return products?.map((product) => {
    const match = consumptionMap.get(product.name);
    return {
      ...product,
      related_data: {
        ...(product.related_data || {}),
        consumptionSchedules: match?.consumptionSchedules || null,
        consumptionRates: match?.consumptionRates || [],
      },
    };
  });
};

export const matchAttributesToProducts = (products, attributeData) => {
  console.log(
    '[matchAttributesToProducts] Input products:',
    products.map((p) => ({ name: p.name, IDFieldList: p.IDFieldList }))
  );
  console.log(
    '[matchAttributesToProducts] Input attributeData:',
    attributeData?.map((p) => ({ name: p.name, IDFieldList: p.IDFieldList }))
  );

  // Initialize every product to have empty arrays for attributes.
  products.forEach((p) => {
    if (!p.related_data) p.related_data = {};
    p.related_data.configurationAttributes = [];
    p.related_data.attributeSets = [];
    p.related_data.productAttributes = []; // Add CPQ attributes support
  });

  if (!attributeData || attributeData.length === 0) {
    console.log('[matchAttributesToProducts] No attribute data provided');
    return products;
  }

  // Create a map for efficient lookup using the canonical key.
  const attributeMap = new Map();
  attributeData.forEach((attrProduct) => {
    const key = createCanonicalKey(attrProduct.IDFieldList);
    console.log('[matchAttributesToProducts] Processing attribute product:', {
      name: attrProduct.name,
      IDFieldList: attrProduct.IDFieldList,
      key,
    });
    if (key) {
      attributeMap.set(key, {
        configurationAttributes: attrProduct.configurationAttributes || [],
        attributeSets: attrProduct.attributeSets || [],
        // Store the entire product attributes object as-is
        productAttributes: [attrProduct],
      });
    }
  });

  console.log('[matchAttributesToProducts] Created attribute map with keys:', Array.from(attributeMap.keys()));

  // Map over the original products and attach the matched attribute data.
  return products.map((product) => {
    const key = createCanonicalKey(product.IDFieldList);
    const matchingAttrs = attributeMap.get(key);
    console.log('[matchAttributesToProducts] Matching product:', {
      name: product.name,
      IDFieldList: product.IDFieldList,
      key,
      found: !!matchingAttrs,
    });

    if (matchingAttrs) {
      // Merge the attribute data into the product's related_data
      product.related_data.configurationAttributes = matchingAttrs.configurationAttributes;
      product.related_data.productAttributes = matchingAttrs.productAttributes;
      console.log(
        '[matchAttributesToProducts] Attached attributes to product:',
        product.name,
        'productAttributes count:',
        product.related_data.productAttributes.length
      );

      // Don't merge productOptions from attributes into top-level productOptions
      // The productOptions from attributes should stay within productAttributes
      // The existing productOptions merging logic is removed to prevent conflicts
    }
    return product;
  });
};

/**
 * Attaches a single RC pricing data object to the corresponding product in a list.
 * @param {Array<object>} products - The main array of product objects.
 * @param {object} pricingObject - The single pricing object from the API.
 * @returns {Array<object>} The updated array of products with pricing data attached.
 */
export const matchPricingToProducts = (products, pricingObject) => {
  // Initialize every product with default empty pricing structures.
  products.forEach((p) => {
    if (!p.related_data) p.related_data = {};
    p.related_data.pricing = {
      attributeBasedAdjustments: [],
      bundleBasedAdjustments: [],
      costBookEntries: [],
      priceAdjustmentTiers: [],
    };
  });

  // If the pricing object is null or doesn't have a productId, we can't match it.
  if (!pricingObject || !pricingObject.productId) {
    return products;
  }

  // Find the one product that this pricing object belongs to.
  const productToUpdate = products.find((p) => (p.sf_id || p.id) === pricingObject.productId);

  // If we found the matching product, attach the pricing data.
  if (productToUpdate) {
    productToUpdate.related_data.pricing = {
      attributeBasedAdjustments: pricingObject.attributeBasedAdjustments || [],
      bundleBasedAdjustments: pricingObject.bundleBasedAdjustments || [],
      costBookEntries: pricingObject.costBookEntries || [],
      priceAdjustmentTiers: pricingObject.priceAdjustmentTiers || [],
    };
  } else {
    console.warn(`Could not find a matching product for pricing data with productId: ${pricingObject.productId}`);
  }

  return products;
};

/**
 * Attaches the RC orchestrator data object to the corresponding product.
 * @param {Array<object>} products - The main array of product objects.
 * @param {Array<object>} orchestratorDataArray - The array of orchestrator objects from the API (even if it only contains one item).
 * @returns {Array<object>} The updated array of products with orchestrator data attached.
 */
export const matchOrchestratorToProducts = (products, orchestratorDataArray) => {
  // Initialize every product with a default null orchestrator object.
  products.forEach((p) => {
    if (!p.related_data) p.related_data = {};
    p.related_data.orchestrator = null; // Initialize as null
  });

  // If the orchestrator data is not a non-empty array, return early.
  if (!orchestratorDataArray || !Array.isArray(orchestratorDataArray) || orchestratorDataArray.length === 0) {
    return products;
  }

  // Since there's one orchestrator per product, we can iterate through the orchestrator data
  // and attach each one to its corresponding product. This handles multiple products in a batch.
  orchestratorDataArray.forEach((orchestratorObject) => {
    if (!orchestratorObject || !orchestratorObject.productId) {
      console.warn('An orchestrator item is missing a productId to match with.', orchestratorObject);
      return; // continue to the next item in the forEach loop
    }

    // Find the product that this orchestrator data belongs to.
    const productToUpdate = products.find((p) => (p.sf_id || p.id) === orchestratorObject.productId);

    if (productToUpdate) {
      // Attach the ENTIRE orchestrator object to this product's related_data.
      productToUpdate.related_data.orchestrator = orchestratorObject;
    } else {
      console.warn(
        `Could not find a matching product for orchestrator data with productId: ${orchestratorObject.productId}`
      );
    }
  });

  return products;
};
